import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { useActionLogSender } from '@framework/action-log';
import { toast } from 'react-hot-toast';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';

type Props = {
  folder: Folder;
  isOpen: boolean;
  hasChildren: boolean;
  onClose(): void;
};

export const FolderDeleteModal: React.FC<Props> = ({ folder, isOpen, hasChildren, onClose }: Props) => {
  const { t } = useTranslation();
  const previousIsOpen = usePrevious(isOpen);
  const actionLogSender = useActionLogSender();

  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && !previousIsOpen) {
      // モーダルを開いた時に、各stateを初期状態に戻す
      setConfirmed(false);
      setIsProcessing(false);
    }
  }, [isOpen, previousIsOpen]);

  const handleSubmit = useCallback(async () => {
    if (!confirmed) return;

    setIsProcessing(true);
    try {
      await ViewModelOperation.deleteFolder(folder.id, actionLogSender);
    } catch (e) {
      toast.error(t('workspace.folderDelete.error'));
      throw e;
    } finally {
      setIsProcessing(false);
    }
    onClose();
  }, [confirmed, onClose, folder.id, actionLogSender, t]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.folderDelete.title')}
      submitButton={
        <Button color="danger" onClick={handleSubmit} disabled={!confirmed} loading={isProcessing}>
          {t('common.action.delete')}
        </Button>
      }
      cancelButton={
        <Button onClick={handleClose} color="secondary" loading={isProcessing}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-2">
        {hasChildren ? (
          <div>
            {t('workspace.folderDelete.hasChildrenWarning', { name: folder.name })}
            <br />
            {t('workspace.folderDelete.cannotRestore')}
            <br />
            {t('workspace.folderDelete.confirmQuestion')}
          </div>
        ) : (
          <div>
            {t('workspace.folderDelete.noChildrenWarning')}
            <br />
            {t('workspace.folderDelete.confirmDelete', { name: folder.name })}
          </div>
        )}

        <label className="block p-2">
          <input
            type="checkbox"
            className="form-checkbox mr-1 size-6 border-brand"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          {t('workspace.modal.deleteConfirmCheck')}
        </label>
      </div>
    </GeneralModal>
  );
};
